"use client";

import React, { useId } from "react";
import { countyKey, countyLabel } from "../../services/zip/county-lookup";
import type { LocalRsnOptions } from "../../services/zip/local-rsn-channels";
import { useZipSession } from "./use-zip-session.client";
import type { ZipSessionCallbacks } from "../../services/zip/zip-session";
import type { ZipStore } from "../../services/zip/zip-store";
import { ZipCode } from "./zip-code.client";
import { withSafeRender } from "../../atoms/safe-render";

const DEFAULT_LOCATION_PLACEHOLDER = "City, State ZIP Code";
/* Production's copy for a failed lookup; the screen has no default of its own. */
const DEFAULT_COUNTY_API_ERROR_MESSAGE =
  "We are having trouble showing local channel data, Please try again later";

/**
 * ZipCodeConnected — the working ZIP Code (WAT-11988): `ZipCode` wired to the ZIP
 * services, so it looks the county up, reads and writes the cookies the older pages
 * share, fetches the regional sports fee and the channels, and stays in step with every
 * other ZIP Code on the page. Endpoints are props, never hosts; with none, production's
 * relative paths.
 *
 * "use client": it runs a lookup session in an effect and reads the shared store with
 * `useSyncExternalStore`. The first render matches the server's (no ZIP). Argued in
 * `design-conflicts/zip-code.mdx`.
 */
export interface ZipCodeConnectedProps {
  /** Ties the trigger, panel and fields together; must be unique on the page. Generated when omitted. */
  id?: string;

  /* ⚠ The three paths below configure the PAGE-WIDE store, not this instance. They read
     as per-instance and are not: a second ZIP Code passing different ones overwrites the
     first's, last writer winning. Pass the same paths everywhere on a page. */
  /** Base path of the channel lookup service, trailing slash included. Defaults to production's. */
  channelLookupPath?: string;
  /** Endpoint the regional sports fee is read from. Defaults to production's. */
  offersPath?: string;
  /** Prefix for `/api/local-channels`: the app's base path, empty when it has none. */
  localChannelsBasePath?: string;
  /** Which channel products to request. Defaults to satellite, as the plan-card topper does. */
  localRsnApiOptions?: LocalRsnOptions;
  /** Fetch local and regional channels with each county lookup. Defaults to true. */
  fetchChannels?: boolean;
  /** Open the panel on a first visit with no known DMA, as production does. Defaults to true. */
  autoOpenOnFirstVisit?: boolean;
  /** Outcomes of the flow, each optional and each called guarded. */
  callbacks?: ZipSessionCallbacks;
  /** A store other than the page-wide one. For isolating a story or a test. */
  store?: ZipStore;

  /** The trigger's lead-in copy. Defaults to the screen's. */
  triggerLabel?: string;
  /** What the trigger reads before any ZIP is known. Defaults to "City, State ZIP Code". */
  noZipLabel?: string;
  /** What the trigger reads when a ZIP is known but no county is. Defaults to "City, State ZIP Code". */
  locationPlaceholder?: string;
  /** An explanation shown as an info icon beside the trigger. */
  tooltipContent?: string;
  tooltipLabel?: string;
  zipLabel?: string;
  zipInputErrorMessage?: string;
  zipLookupErrorMessage?: string;
  countyLabel?: string;
  countyPlaceholder?: string;
  countySelectionErrorMessage?: string;
  /** Shown when the county lookup fails. Defaults to production's copy. */
  countyApiErrorMessage?: string;
  /** Shown while the lookup is slow. Defaults to the screen's. */
  lookupHangingMessage?: string;
  successMessage?: string;
  ctaLabel?: string;
  addLocationCtaLabel?: string;
  ctaLinkPosition?: string;
  /** The ZIP field's and the county dropdown's border colour. Any CSS colour; unset, the Figma colour. */
  fieldStrokeColor?: string;
  /** The same border on hover. Set both. */
  fieldStrokeHoverColor?: string;

  className?: string;
}

function ZipCodeConnectedImpl({
  id: idProp,
  channelLookupPath,
  offersPath,
  localChannelsBasePath,
  localRsnApiOptions,
  fetchChannels,
  autoOpenOnFirstVisit,
  callbacks,
  store,
  locationPlaceholder = DEFAULT_LOCATION_PLACEHOLDER,
  countyApiErrorMessage = DEFAULT_COUNTY_API_ERROR_MESSAGE,
  ...copy
}: ZipCodeConnectedProps) {
  const generatedId = useId();
  const id = idProp || generatedId;
  const { session, state } = useZipSession(
    {
      localRsnApiOptions,
      fetchChannels,
      autoOpenOnFirstVisit,
      callbacks
    },
    {
      channelLookupPath,
      offersPath,
      localChannelsBasePath
    },
    store
  );

  const counties = state.countyOptions.map(county => ({
    value: countyKey(county),
    label: countyLabel(county)
  }));

  /* The pill names the county and ZIP once both are known. A ZIP with no county
     (an edge ZIP whose lookup failed) reads as the placeholder, as in production. */
  let locationText: string | undefined;
  if (state.displayZip) {
    locationText = state.displayCounty ?
      `${state.displayCounty.countyName}, ${state.displayCounty.state} ${state.displayZip}` :
      locationPlaceholder;
  }

  return (
    <ZipCode
      {...copy}
      apiErrorMessage={state.countyApiError ? countyApiErrorMessage : undefined}
      counties={counties}
      countySelectionError={state.countySelectionError}
      expanded={state.isExpanded}
      id={id}
      inputValue={state.inputZip}
      isCountyLoading={state.isCountyApiLoading}
      isLookupHanging={state.countyApiHanging}
      locationText={locationText}
      onClear={session.clear}
      onCountySelect={session.selectCounty}
      onSubmit={session.submit}
      onToggle={session.setExpanded}
      onZipChange={session.changeZip}
      selectedCounty={state.selectedCountyKey}
      showSuccessMessage={state.showSuccess}
      validateOnSubmit={false}
      zip={state.displayZip}
      zipInputError={state.zipInputError}
      zipNoResults={state.countyNoResults}
    />
  );
}
export const ZipCodeConnected = withSafeRender(ZipCodeConnectedImpl, "ZipCodeConnected");

export default ZipCodeConnected;
